import { PropsWithChildren, ReactNode } from 'react';

import { ChevronDown, SearchIcon } from '../assets';
import { cn } from '../lib';
import { Input, InputProps } from './components/input';
import { DashboardDialog, DashboardDialogProps } from './dashboard-dialog';
import { Label } from './label';
import { ScrollArea } from './scroll-area';

interface SelectDialogProps
  extends PropsWithChildren,
  Omit<DashboardDialogProps, 'content'> {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  customTrigger?: ReactNode;
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
  placeholder: string;
  hideCloseButton?: boolean;
}

const Content = ({
  searchValue,
  setSearchValue,
  children,
  placeholder,
}: PropsWithChildren<
  Pick<SelectDialogProps, 'searchValue' | 'setSearchValue' | 'placeholder'>
>) => {
  return (
    <div className="flex max-w-full flex-col border-none md:max-w-[342px]">
      <div className="p-5">
        <Label className="relative flex h-fit w-full flex-col">
          <SearchIcon className="absolute left-[6px] top-1/2 -translate-y-1/2" />
          <input
            className="rounded-xl bg-base-300 px-[6px] py-2 pl-8 text-base outline-none placeholder:text-base-100"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            placeholder={placeholder}
          />
        </Label>
      </div>
      <ScrollArea className="flex">{children}</ScrollArea>
    </div>
  );
};

interface DefaultTriggerProps extends InputProps {
  value: string;
  placeholder: string;
  isOpen?: boolean;
  className?: string;
  iconColor?: string;
  inputClassName?: string;
  variant?:
  | 'default'
  | 'small'
  | 'dashboard'
  | 'secondary'
  | 'send'
  | null
  | undefined;
}

export const DashboardSelectDialogTrigger = ({
  isOpen,
  className,
  inputClassName,
  iconColor,
  variant = 'dashboard',
  ...props
}: DefaultTriggerProps) => {
  return (
    <Input
      readOnly
      className={cn('pointer-events-none', className)}
      inputClassName={cn('cursor-pointer text-left', inputClassName)}
      type='button'
      variant={variant}
      afterIcon={
        <ChevronDown
          color={iconColor}
          className={cn(
            'pointer-events-none absolute right-2 top-1/2 size-6 !-translate-y-1/2 transition-transform',
            {
              'rotate-180': isOpen,
            },
          )}
        />
      }
      {...props}
    />
  );
};

export const DashboardSelectDialog = ({
  isOpen,
  setIsOpen,
  searchValue,
  setSearchValue,
  children,
  customTrigger,
  placeholder,
  hideCloseButton = true,
  ...props
}: SelectDialogProps) => {
  return (
    <DashboardDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      hideCloseButton={hideCloseButton}
      headerProps={{ className: 'h-0 min-h-0 p-0' }}
      animationProps={{ isFirst: true }}
      content={
        <Content
          placeholder={placeholder}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        >
          {children}
        </Content>
      }
      {...props}
    >
      {customTrigger}
    </DashboardDialog>
  );
};
